import { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'selah-install-dismissed-at';
const DISMISS_DAYS = 14; // Don't pester within 14 days of dismissal

/**
 * PWA install prompt card. Two display paths:
 *  1. Android / Desktop Chrome / Edge — captures the `beforeinstallprompt` event
 *     and offers a one-tap install button.
 *  2. iOS Safari — there's no programmatic install, so we show step-by-step
 *     instructions ("Tap Share → Add to Home Screen").
 *
 * Hidden when (a) already installed, (b) dismissed in the last 14 days,
 * (c) running inside the installed PWA (display-mode: standalone).
 */
export const InstallPromptCard = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed → hide
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Recently dismissed → hide
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }

    // Detect iOS Safari (PWAs install differently there)
    const ua = window.navigator.userAgent.toLowerCase();
    const iosDevice = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
    if (iosDevice) {
      setIsIos(true);
      setVisible(true);
      return;
    }

    // Android / Chromium — wait for the install prompt event
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Card
      className="mb-6 border-[#B76E79]/30 bg-gradient-to-br from-[#36454F] to-[#2a3640] relative overflow-hidden animate-fade-in-up"
      data-testid="install-prompt-card"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="install-prompt-dismiss"
        className="absolute top-3 right-3 text-[#E0BFB8]/60 hover:text-[#E0BFB8] transition-colors"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 rounded-sm bg-[#B76E79]/20 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-[#E0BFB8]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#E0BFB8] mb-1">
              Keep Selah Slate one tap away
            </h3>
            {isIos ? (
              <div className="text-sm text-white/75 font-['Manrope'] leading-relaxed space-y-1.5">
                <p>Install on your iPhone for the full app experience:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-white/65 ml-1">
                  <li>Tap the <strong>Share</strong> icon at the bottom of Safari</li>
                  <li>Scroll and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>Add</strong> in the top-right corner</li>
                </ol>
              </div>
            ) : (
              <>
                <p className="text-sm text-white/75 font-['Manrope'] leading-relaxed">
                  Install Selah Slate to your home screen — opens like a native
                  app, works offline, and feels exactly the same. No app-store
                  download needed.
                </p>
                <Button
                  onClick={handleInstall}
                  data-testid="install-prompt-action"
                  className="mt-3 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Install app
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstallPromptCard;
